import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiCard, UiCardHeader, UiCardBody, UiBreadcrumb, UiPagination, UiSelect } from 'components/common'
import { Box, Button, Card, CardBody, Flex, Grid, GridItem, Heading, Select, Spinner, Text, useDisclosure } from '@chakra-ui/react'
import { useDebouncedCallback } from 'use-debounce'
import { getMyTasks, getUsersByRoles } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { ProjectSectionTaskRow } from '../project-detail/tasks/ProjectSectionTaskRow'
import { ModalRemoveTask, ModalTask, ModalTaskType } from '../project-detail/tasks/ModalTask'
import { DrawerProjectSectionTaskNote } from '../project-detail/tasks/DrawerProjectSectionTaskNote'
import { ROLES } from 'lib'
import { format as formatDate } from 'date-fns'
import { useNavigate } from 'react-router-dom'

const BreadcrumbNav = [
  {
    label: 'My Tasks',
    isCurrentPage: true
  }
]

const MY_TASKS_FILTER_OPTIONS = {
  All: 'All Tasks',
  Due: 'Due Tasks',
  PastDue: 'Past Due Tasks'
}

export const MY_TASKS_PAGE_TYPE = {
  All: 'All',
  Overdue: 'Overdue',
  Duetoday: 'Due Today'
}

export const MyTasks = ({ pageType }) => {
  const { currentUser, createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 20,
    search: '',
    orderBy: {
      key: 'dueDate',
      direction: 'asc'
    }
  })
  const [isLoadingTasks, setIsLoadingTasks] = useState(false)
  const [data, setData] = useState(null)
  const [rows, setRows] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)

  const [selectedFilter, setSelectedFilter] = useState(MY_TASKS_FILTER_OPTIONS.All)

  const disclosureTask = useDisclosure()
  const disclosureRemoveTask = useDisclosure()
  const [selectedTask, setSelectedTask] = useState(null)

  const disclosureTaskNotes = useDisclosure()
  const [selectedTaskIdForNotes, setSelectedTaskIdForNotes] = useState(0)

  const [keyForNotesReload, setKeyForNotesReload] = useState(0)

  const navigate = useNavigate()

  useEffect(() => {
    setSelectedUser({ ...currentUser })
  }, [currentUser])

  const load = async () => {
    if (!selectedUser || !selectedUser.id) {
      return
    }

    setIsLoadingTasks(true)
    try {
      const response = await getMyTasks(selectedUser.id)
      setData(response)
      setTableConfig(draft => {
        draft.totalRecords = response.tasks.length
        draft.totalPages = Math.ceil(draft.totalRecords / draft.itemsPerPage)
      })
      setIsLoadingTasks(false)
    } catch (err) {
      console.log(err)
      createToast('error', 'Error', 'Failed to load tasks.')
      setIsLoadingTasks(false)
    }
  }

  useEffect(() => {
    load()
  }, [selectedUser])

  const customizedRows = useMemo(() => {
    if (!data?.tasks) {
      return []
    }

    let result = []

    if (pageType === MY_TASKS_PAGE_TYPE.All) {
      if (selectedFilter === MY_TASKS_FILTER_OPTIONS.All) {
        result = data.tasks.filter(v => !!v.dueDate)
      } else if (selectedFilter === MY_TASKS_FILTER_OPTIONS.Due) {
        result = data.tasks.filter(v => {
          if (!v?.dueDate) {
            return false
          }
          const dueDate = formatDate(new Date(new Date(v.dueDate).getTime() + new Date(v.dueDate).getTimezoneOffset() * 60000), 'yyyy-MM-dd')
          return dueDate === formatDate(new Date(), 'yyyy-MM-dd')
        })
      } else if (selectedFilter === MY_TASKS_FILTER_OPTIONS.PastDue) {
        result = data.tasks.filter(v => {
          if (!v?.dueDate) {
            return false
          }
          const dueDate = formatDate(new Date(new Date(v.dueDate).getTime() + new Date(v.dueDate).getTimezoneOffset() * 60000), 'yyyy-MM-dd')
          return dueDate < formatDate(new Date(), 'yyyy-MM-dd')
        })
      }
    } else if (pageType === MY_TASKS_PAGE_TYPE.Overdue) {
      result = data.tasks.filter(v => {
        if (!v?.dueDate) {
          return false
        }
        const dueDate = formatDate(new Date(new Date(v.dueDate).getTime() + new Date(v.dueDate).getTimezoneOffset() * 60000), 'yyyy-MM-dd')
        return dueDate < formatDate(new Date(), 'yyyy-MM-dd')
      })
    } else {
      result = data.tasks.filter(v => {
        if (!v?.dueDate) {
          return false
        }
        const dueDate = formatDate(new Date(new Date(v.dueDate).getTime() + new Date(v.dueDate).getTimezoneOffset() * 60000), 'yyyy-MM-dd')
        return dueDate === formatDate(new Date(), 'yyyy-MM-dd')
      })
    }
    setTableConfig(draft => {
      draft.totalRecords = result.length
      draft.totalPages = Math.ceil(draft.totalRecords / draft.itemsPerPage)
    })
    return result
  }, [data?.tasks, selectedFilter, pageType])

  const filter = () => {
    if (!data) {
      return
    }

    setIsLoadingTasks(true)
    setRows(customizedRows.slice((tableConfig.curPage - 1) * tableConfig.itemsPerPage, tableConfig.curPage * tableConfig.itemsPerPage))
    setIsLoadingTasks(false)
  }

  const debouncedFilter = useDebouncedCallback(filter, 400)

  useEffect(() => {
    debouncedFilter()
  }, [customizedRows, tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onEditTask = (task) => {
    disclosureTask.onOpen()
    setSelectedTask({ ...task })
  }

  const onRemoveTask = (task) => {
    disclosureRemoveTask.onOpen()
    setSelectedTask({ ...task })
  }

  const onOpenTaskNotes = (taskId) => {
    setSelectedTaskIdForNotes(taskId)
    disclosureTaskNotes.onOpen()
  }

  const handleChangeFilter = (e) => {
    setSelectedFilter(e.target.value)
  }

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box my={8}>
        {(currentUser?.role === ROLES.ProjectManager || currentUser?.role === ROLES.SalesUserProjectManager || currentUser?.role === ROLES.Admin) && (
          <Flex gap={8} alignItems="center" mb={8}>
            <Text fontWeight="bold">Project Manager :</Text>
            <UiSelect
              value={selectedUser}
              onChange={v => {
                setSelectedUser({ ...v })
              }}
              loadData={(page, search) => getUsersByRoles(page, 20, search, 'name', 'asc', [ROLES.ProjectManager, ROLES.SalesUserProjectManager, ROLES.Admin, ROLES.SalesUser], true)}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => v.name}
              placeholder="Search Users"
              noOptionsMessage={() => 'No Users Found'}
              required={true}
              rootProps={{ w: '300px' }}
            />
          </Flex>
        )}
        {data !== null ? (
          <>
            {pageType === MY_TASKS_PAGE_TYPE.All && (
              <Grid
                templateColumns={{ lg: 'repeat(6, 1fr)', md: 'repeat(3, 1fr)' }}
                gap={4}
                mb={8}
              >
                <GridItem>
                  <Card textAlign="center" h="100%">
                    <CardBody display="flex" flexDirection="column" justifyContent="space-between" cursor="pointer" onClick={() => navigate('/projects/my-tasks/overdue')}>
                      <Text>Overdue Tasks</Text>
                      <Text fontWeight="bold" fontSize="lg">{data.overdueTaskCount}</Text>
                    </CardBody>
                  </Card>
                </GridItem>
                <GridItem>
                  <Card textAlign="center" h="100%">
                    <CardBody display="flex" flexDirection="column" justifyContent="space-between" cursor="pointer" onClick={() => navigate('/projects/my-tasks/due-today')}>
                      <Text>Tasks Due Today</Text>
                      <Text fontWeight="bold" fontSize="lg">{data.todayTaskCount}</Text>
                    </CardBody>
                  </Card>
                </GridItem>
              </Grid>
            )}
            <UiCard borderRadius={0}>
              <UiCardHeader bg="gray.500" p={4}>
                <Heading color="white" size="sm">My Tasks - {pageType}</Heading>
              </UiCardHeader>
              <UiCardBody p={8}>
                {pageType === MY_TASKS_PAGE_TYPE.All ? (
                  <Select value={selectedFilter} onChange={handleChangeFilter} mb={8} w="fit-content">
                    <option value={MY_TASKS_FILTER_OPTIONS.All}>{MY_TASKS_FILTER_OPTIONS.All}</option>
                    <option value={MY_TASKS_FILTER_OPTIONS.Due}>{MY_TASKS_FILTER_OPTIONS.Due}</option>
                    <option value={MY_TASKS_FILTER_OPTIONS.PastDue}>{MY_TASKS_FILTER_OPTIONS.PastDue}</option>
                  </Select>
                ) : (
                  <Button colorScheme="blue" size="sm" mb={8} onClick={() => navigate('/projects/my-tasks')}>Back to My Tasks Page</Button>
                )}
                {isLoadingTasks ? (
                  <Flex alignItems="center" gap={4}>
                    <Spinner color="blue.400" size="sm" />
                    <Text fontSize="sm">Loading ...</Text>
                  </Flex>
                ) : (
                  rows.length > 0 ? (
                    <>
                      <Grid
                        templateColumns="repeat(15, 1fr)"
                        fontWeight="bold"
                        fontSize="sm"
                        borderBottomWidth={1}
                        borderBottomStyle="solid"
                        borderBottomColor="gray.200"
                        pl={8}
                        pr={4}
                        pb={2}
                        gap={4}
                      >
                        <GridItem colSpan={6}>Task name</GridItem>
                        {/* <GridItem colSpan={2}>Assignee</GridItem> */}
                        <GridItem colSpan={2}>Due Date</GridItem>
                        <GridItem colSpan={4}>Project</GridItem>
                        <GridItem colSpan={1}></GridItem>
                      </Grid>
                      {rows.map(row => {
                        return (
                          <ProjectSectionTaskRow
                            key={row.id}
                            task={row}
                            onEditTask={onEditTask}
                            onRemoveTask={onRemoveTask}
                            reload={load}
                            onOpenTaskNotes={onOpenTaskNotes}
                            showProjectName={true}
                            showAssigneeName={false}
                          />
                        )
                      })}
                    </>
                  ) : (
                    <Text>No tasks available</Text>
                  )
                )}
                <UiPagination
                  pt={8}
                  curPage={tableConfig.curPage}
                  totalPages={tableConfig.totalPages}
                  onSelect={(v) => setTableConfig(draft => { draft.curPage = v })}
                />
              </UiCardBody>
            </UiCard>
          </>
        ) : (
          <Text>No data available.</Text>
        )}
        {disclosureTask.isOpen && (
          <ModalTask
            modalType={!selectedTask?.id ? ModalTaskType.Add : ModalTaskType.Edit}
            projectId={selectedTask?.projectId}
            task={selectedTask}
            reload={() => {
              setSelectedTask(null)
              load()
              if (selectedTaskIdForNotes === selectedTask?.id) {
                setKeyForNotesReload(keyForNotesReload + 1)
              }
            }}
            {...disclosureTask}
          />
        )}
        {disclosureRemoveTask.isOpen && (
          <ModalRemoveTask
            task={selectedTask}
            reload={() => {
              setSelectedTask(null)
              load()
            }}
            {...disclosureRemoveTask}
          />
        )}
        {disclosureTaskNotes.isOpen && (
          <DrawerProjectSectionTaskNote
            keyForReload={keyForNotesReload}
            taskId={selectedTaskIdForNotes}
            onEditTask={onEditTask}
            {...disclosureTaskNotes}
          />
        )}
      </Box>
    </>
  )
}

MyTasks.propTypes = {
  pageType: PropTypes.string
}
